import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader, type Crumb } from '../components/PageHeader';
import { useNav, workspacePath } from '../nav';
import { packageInstallUrl } from '../salesforce';
import { useSession } from '../session';

/**
 * Where Salesforce sends somebody back to when connecting an org did not work.
 *
 * The callback names the workspace the connection was for when it got far enough to know. When it did not
 * (Salesforce refused before handing back our state), the way back uses the workspace the menu is showing:
 * the last one visited, which is in practice the one whose connect page sent them there. Either way it is
 * only honoured if it is a workspace of theirs, and the crumbs say which.
 */
export function ConnectError() {
  const [params] = useSearchParams();
  const { me } = useSession();
  const nav = useNav();
  const named = params.get('workspace');
  const tenantId = named && me?.memberships.some((m) => m.tenantId === named) ? named : nav.tenantId;
  const reason = params.get('reason') ?? 'The connection did not complete.';
  const notApproved = /admin approved/i.test(reason);
  // The callback tells us which environment was being connected; fall back to production when it cannot.
  const environment = params.get('env') === 'sandbox' ? 'sandbox' : 'production';
  // Only tagged when the callback said so: a guessed "Production" in the header would be read as a fact.
  const environmentKnown = params.has('env');

  const membership = me?.memberships.find((m) => m.tenantId === tenantId);
  // Only owners and admins connect orgs, so only they are offered another go.
  const canConnect = membership?.role === 'owner' || membership?.role === 'admin';
  const crumbs: Crumb[] | undefined =
    membership && tenantId
      ? [
          { label: membership.name, to: workspacePath(tenantId) },
          { label: 'Salesforce orgs', to: workspacePath(tenantId) },
          ...(canConnect ? [{ label: 'Connect an org', to: workspacePath(tenantId, 'orgs/new') }] : []),
        ]
      : undefined;

  return (
    <>
      <PageHeader
        crumbs={crumbs}
        title="Connection failed"
        meta={
          environmentKnown && (
            <span className={`env-tag ${environment}`}>{environment === 'sandbox' ? 'Sandbox' : 'Production'}</span>
          )
        }
      />
      <div className="card measure">
        <div className="alert">{reason}</div>
        {notApproved ? (
          <p>
            Salesforce says the user you signed in as is not pre-authorised for the Kruvi Access app. This happens when the
            org has already been set up for Kruvi Access: from then on, only users holding the Kruvi Access permission set can
            connect. Assign that permission set to yourself in the org, then try again.
          </p>
        ) : (
          <p>
            Common causes: the Kruvi Access package is not installed in that org, you signed in as a user without admin rights,
            or the permission set is not assigned. Fix it in Salesforce, then try connecting again. If the package is
            missing, install it in the {environment} org you were connecting to.
          </p>
        )}
        <div className="actions">
          {tenantId && canConnect ? (
            <Link className="button" to={workspacePath(tenantId, 'orgs/new')}>Try again</Link>
          ) : (
            <Link className="button" to={tenantId ? workspacePath(tenantId) : '/'}>
              {tenantId ? 'Back to Salesforce orgs' : 'Back to Kruvi Access'}
            </Link>
          )}
          {!notApproved && (
            <a className="button secondary" href={packageInstallUrl(environment)} target="_blank" rel="noopener noreferrer">
              Install the Kruvi Access package
            </a>
          )}
          {tenantId && canConnect && (
            <Link className="button secondary" to={workspacePath(tenantId)}>Back to Salesforce orgs</Link>
          )}
        </div>
      </div>
    </>
  );
}
